import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ObligationVisualizer from '../components/ObligationVisualizer';
import LegalChatAssistant from '../components/LegalChatAssistant';
import VersionHistory from '../components/VersionHistory';
import BlockchainDeployment from '../components/BlockchainDeployment';
import CollaborationPanel from '../components/CollaborationPanel';
import CollaborativeCursors from '../components/CollaborativeCursors';
import BiometricSignature from '../components/BiometricSignature';
import MultiSignature from '../components/MultiSignature';
import ClauseAdvisorPanel from '../components/ClauseAdvisorPanel';
import ContractNotesAndTags from '../components/ContractNotesAndTags';
import ContractDeadlines from '../components/ContractDeadlines';
import ActivityLog from '../components/ActivityLog';
import {
  Shield,
  ArrowLeft,
  Download,
  PenTool,
  BarChart3,
  FileText,
  Scale,
  Cpu,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Eye,
  X,
  Trash2,
  GitBranch,
  Bell,
  History,
  Rocket,
  Edit3,
  Users,
  Fingerprint,
  FileType,
  LayoutTemplate,
  Gavel,
  Calendar,
  StickyNote,
  Activity
} from 'lucide-react';

const ContractViewPage = () => {
  const { contractId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const documentContainerRef = useRef(null);
  
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('document');
  const [analysisData, setAnalysisData] = useState(null);
  const [simplifiedData, setSimplifiedData] = useState(null);
  const [biasData, setBiasData] = useState(null);
  const [smartContractData, setSmartContractData] = useState(null);
  const [visualizationData, setVisualizationData] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [contractAlerts, setContractAlerts] = useState(null);
  const [processingJob, setProcessingJob] = useState(null);
  
  // Signature state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showBiometricModal, setShowBiometricModal] = useState(false);
  const [showClauseAdvisor, setShowClauseAdvisor] = useState(false);
  const [signerName, setSignerName] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    fetchContract();
    fetchContractAlerts();
  }, [contractId]);

  const fetchContract = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}`);
      setContract(response.data);
    } catch (error) {
      console.error('Error fetching contract:', error);
      toast.error('Error al cargar el contrato');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchContractAlerts = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/alerts`);
      setContractAlerts(response.data);
    } catch (error) {
      console.error('Error fetching contract alerts:', error);
    }
  };

  const pollJob = useCallback(async (jobId, onComplete, onFail) => {
    try {
      const response = await api.get(`/jobs/${jobId}`);
      const job = response.data;
      
      if (job.status === 'completed') {
        setProcessingJob(null);
        onComplete(job.result);
      } else if (job.status === 'failed') {
        setProcessingJob(null);
        onFail(job.error || 'Error en el procesamiento');
      } else {
        setTimeout(() => pollJob(jobId, onComplete, onFail), 2000);
      }
    } catch (error) {
      setProcessingJob(null);
      onFail('Error al consultar el estado');
    }
  }, [api]);

  const handleAnalyze = async () => {
    setProcessingJob('analyze');
    try {
      const response = await api.post(`/contracts/${contractId}/analyze`);
      pollJob(
        response.data.job_id,
        (result) => {
          setAnalysisData(result);
          fetchContract(); // Refresh to get updated conflict score
          toast.success('Análisis completado');
        },
        (error) => toast.error(error)
      );
    } catch (error) {
      setProcessingJob(null);
      toast.error('Error al iniciar análisis');
    }
  };

  const handleSimplify = async () => {
    setProcessingJob('simplify');
    try {
      const response = await api.post(`/contracts/${contractId}/simplify`);
      pollJob(
        response.data.job_id,
        (result) => {
          setSimplifiedData(result);
          toast.success('Simplificación completada');
        },
        (error) => toast.error(error)
      );
    } catch (error) {
      setProcessingJob(null);
      toast.error('Error al iniciar simplificación');
    }
  };

  const handleDetectBias = async () => {
    setProcessingJob('bias');
    try {
      const response = await api.post(`/contracts/${contractId}/detect-bias`);
      pollJob(
        response.data.job_id,
        (result) => {
          setBiasData(result);
          toast.success('Análisis de equidad completado');
        },
        (error) => toast.error(error)
      );
    } catch (error) {
      setProcessingJob(null);
      toast.error('Error al iniciar detección de sesgo');
    }
  };

  const handleSmartContract = async () => {
    setProcessingJob('smart');
    try {
      const response = await api.post(`/contracts/${contractId}/smart-contract`);
      pollJob(
        response.data.job_id,
        (result) => {
          setSmartContractData(result);
          toast.success('Smart Contract generado');
        },
        (error) => toast.error(error)
      );
    } catch (error) {
      setProcessingJob(null);
      toast.error('Error al generar Smart Contract');
    }
  };

  const handleVisualize = async () => {
    setProcessingJob('visualize');
    try {
      const response = await api.post(`/contracts/${contractId}/visualize`);
      pollJob(
        response.data.job_id,
        (result) => {
          setVisualizationData(result);
          toast.success('Visualización generada');
        },
        (error) => toast.error(error)
      );
    } catch (error) {
      setProcessingJob(null);
      toast.error('Error al generar visualización');
    }
  };

  const handleVerify = async () => {
    setProcessingJob('verify');
    try {
      const response = await api.post(`/contracts/${contractId}/verify`);
      setVerificationData(response.data);
      toast.success('Verificación completada');
    } catch (error) {
      toast.error('Error al verificar activos');
    } finally {
      setProcessingJob(null);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/export-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_${contractId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al exportar PDF');
    }
  };

  const handleExportWord = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/export-docx`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_${contractId.slice(0, 8)}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Word descargado');
    } catch (error) {
      console.error('Error exporting Word:', error);
      toast.error('Error al exportar Word');
    }
  };

  const handleApplyClauses = async (clauses) => {
    // Build clause text from templates
    const clauseTexts = clauses.map(c => c.template || '').filter(t => t);
    
    if (clauseTexts.length === 0) {
      toast.error('Las cláusulas seleccionadas no tienen texto modelo');
      return;
    }
    
    // Append clauses to contract content
    const newContent = contract.content + '\n\n' + 
      '=== CLÁUSULAS PROTECTORAS ADICIONALES ===\n\n' +
      clauseTexts.join('\n\n');
    
    try {
      const response = await api.put(`/contracts/${contractId}`, {
        content: newContent
      });
      setContract(prev => ({ ...prev, content: newContent }));
      toast.success(`${clauses.length} cláusula(s) agregada(s) al contrato`);
    } catch (error) {
      console.error('Error applying clauses:', error);
      toast.error('Error al aplicar cláusulas');
    }
  };

  // Signature Canvas Functions
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
    }
  }, []);

  useEffect(() => {
    if (showSignatureModal) {
      setTimeout(initCanvas, 100);
    }
  }, [showSignatureModal, initCanvas]);

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const coords = getCanvasCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const coords = getCanvasCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    initCanvas();
  };

  const handleSign = async () => {
    if (!signerName.trim()) {
      toast.error('Ingresa tu nombre para firmar');
      return;
    }

    const canvas = canvasRef.current;
    const signatureImage = canvas.toDataURL('image/png');

    try {
      await api.post(`/contracts/${contractId}/sign`, {
        signature_image: signatureImage,
        signer_name: signerName.trim()
      });
      
      toast.success('Contrato firmado exitosamente');
      setShowSignatureModal(false);
      fetchContract();
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error('Error al firmar el contrato');
    }
  };

  const handleBiometricSignatureComplete = async (signatureData) => {
    try {
      // Update contract with biometric signature info
      await api.post(`/contracts/${contractId}/sign`, {
        signature_type: 'biometric_advanced',
        verification_id: signatureData.verification_id,
        signer_name: user?.username || user?.email || 'Usuario',
        biometric_verified: signatureData.user_verified
      });
      
      toast.success('Contrato firmado con verificación biométrica');
      setShowBiometricModal(false);
      fetchContract();
    } catch (error) {
      console.error('Error saving biometric signature:', error);
      toast.error('Error al guardar la firma biométrica');
    }
  };

  const tabs = [
    { id: 'document', label: 'Documento', icon: FileText },
    { id: 'signers', label: 'Firmantes', icon: Users },
    { id: 'clauses', label: 'Cláusulas', icon: Gavel },
    { id: 'analysis', label: 'Análisis', icon: BarChart3 },
    { id: 'simplify', label: 'Simplificar', icon: Eye },
    { id: 'bias', label: 'Equidad', icon: Scale },
    { id: 'visualize', label: 'Obligaciones', icon: GitBranch },
    { id: 'smart', label: 'Smart', icon: Cpu },
    { id: 'blockchain', label: 'Blockchain', icon: Rocket },
    { id: 'versions', label: 'Versiones', icon: History },
    { id: 'collaborators', label: 'Colaborar', icon: Users },
    { id: 'verify', label: 'Verificar', icon: CheckCircle2 },
    { id: 'alerts', label: 'Alertas', icon: Bell },
    { id: 'deadlines', label: 'Fechas', icon: Calendar },
    { id: 'notes', label: 'Notas', icon: StickyNote },
    { id: 'activity', label: 'Actividad', icon: Activity },
  ];

  if (loading) {
    return (
      <div className="min-h-screen mesh-gradient flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Mis Contratos</span>
            </Link>
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-blue-500" />
              <span className="font-semibold font-['Outfit']">NotaryOS</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Contract Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Outfit']">{contract?.title}</h1>
            <div className="flex items-center space-x-3 mt-2">
              <span className={contract?.is_signed ? 'status-success' : 'status-neutral'}>
                {contract?.is_signed ? 'Firmado' : contract?.status}
              </span>
              {contract?.conflict_score !== null && (
                <span className={
                  contract.conflict_score < 30 ? 'status-success' :
                  contract.conflict_score < 60 ? 'status-warning' : 'status-error'
                }>
                  Riesgo: {contract.conflict_score}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <button
              onClick={handleExportPDF}
              data-testid="export-pdf-btn"
              className="flex items-center space-x-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>PDF</span>
            </button>
            <button
              onClick={handleExportWord}
              data-testid="export-word-btn"
              className="flex items-center space-x-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <FileType className="w-4 h-4" />
              <span>Word</span>
            </button>
            {!contract?.is_signed && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowBiometricModal(true)}
                  data-testid="biometric-sign-btn"
                  className="btn-primary px-4 py-2 flex items-center space-x-2"
                >
                  <Fingerprint className="w-4 h-4" />
                  <span>Firma Biométrica</span>
                </button>
                <button
                  onClick={() => setShowSignatureModal(true)}
                  data-testid="sign-contract-btn"
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  <PenTool className="w-4 h-4" />
                  <span>Firma Manual</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto pb-2 mb-6 gap-2 scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg whitespace-nowrap transition-all text-sm min-w-fit flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4 flex-shrink-0" />
              <span className="truncate max-w-[100px] sm:max-w-none">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Document View (always visible on left) */}
          <div 
            ref={documentContainerRef}
            className={`${activeTab === 'document' ? 'lg:col-span-3' : 'lg:col-span-2'} bg-card border border-border rounded-xl p-6 relative`}
          >
            <h2 className="text-lg font-semibold font-['Outfit'] mb-4 flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-400" />
              <span>Contenido del Contrato</span>
            </h2>
            <div className="prose prose-invert max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-slate-300 font-sans leading-relaxed bg-slate-900/50 p-4 rounded-lg max-h-[600px] overflow-y-auto">
                {contract?.content}
              </pre>
            </div>
            
            {/* Collaborative Cursors */}
            <CollaborativeCursors 
              contractId={contractId}
              containerRef={documentContainerRef}
            />
          </div>

          {/* Analysis Panel */}
          {activeTab !== 'document' && (
            <div className="bg-card border border-border rounded-xl p-6">
              {activeTab === 'signers' && (
                <div data-testid="signers-panel">
                  <MultiSignature 
                    contractId={contractId}
                    contractTitle={contract?.title}
                    onUpdate={fetchContract}
                  />
                </div>
              )}

              {activeTab === 'clauses' && (
                <div data-testid="clauses-panel">
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-4 flex items-center space-x-2">
                    <Gavel className="w-5 h-5 text-cyan-400" />
                    <span>Asesor de Cláusulas Protectoras</span>
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">
                    Obtén recomendaciones de cláusulas que te protegen según tu rol en el contrato y la normativa chilena vigente.
                  </p>
                  <button 
                    onClick={() => setShowClauseAdvisor(true)} 
                    data-testid="open-clause-advisor-btn" 
                    className="btn-primary w-full py-3 flex items-center justify-center gap-2"
                  >
                    <Scale className="w-5 h-5" />
                    Abrir Asesor de Cláusulas
                  </button>
                  
                  {contract?.added_clauses?.length > 0 && (
                    <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                      <h4 className="text-sm font-medium text-emerald-400 mb-2">Cláusulas agregadas:</h4>
                      <ul className="text-sm text-slate-300 space-y-1">
                        {contract.added_clauses.map((clause, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            {clause}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'analysis' && (
                <div data-testid="analysis-panel">
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-4 flex items-center space-x-2">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    <span>Análisis de Riesgo</span>
                  </h3>
                  {!analysisData && processingJob !== 'analyze' && (
                    <button onClick={handleAnalyze} data-testid="run-analysis-btn" className="btn-primary w-full py-2">
                      Ejecutar Análisis
                    </button>
                  )}
                  {processingJob === 'analyze' && (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-2" />
                      <p className="text-sm text-slate-400">Analizando contrato...</p>
                    </div>
                  )}
                  {analysisData && (
                    <div className="space-y-4">
                      <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                        <div className="text-4xl font-bold text-blue-400 mb-1">{analysisData.conflict_score}</div>
                        <div className="text-sm text-slate-400">Puntaje de Conflicto</div>
                        <div className={`mt-2 ${
                          analysisData.risk_level === 'bajo' ? 'status-success' :
                          analysisData.risk_level === 'medio' ? 'status-warning' : 'status-error'
                        }`}>
                          Riesgo {analysisData.risk_level}
                        </div>
                      </div>
                      {analysisData.risk_factors?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-400 mb-2">Factores de Riesgo:</h4>
                          <div className="space-y-2">
                            {analysisData.risk_factors.map((factor, i) => (
                              <div key={i} className="p-3 bg-slate-900/50 rounded-lg text-sm">
                                <div className="flex items-start space-x-2">
                                  <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                                    factor.severity === 'alto' ? 'text-rose-400' :
                                    factor.severity === 'medio' ? 'text-amber-400' : 'text-slate-400'
                                  }`} />
                                  <div>
                                    <p className="text-slate-300">{factor.risk}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'simplify' && (
                <div data-testid="simplify-panel">
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-4 flex items-center space-x-2">
                    <Eye className="w-5 h-5 text-cyan-400" />
                    <span>Traducción Simple</span>
                  </h3>
                  {!simplifiedData && processingJob !== 'simplify' && (
                    <button onClick={handleSimplify} data-testid="run-simplify-btn" className="btn-primary w-full py-2">
                      Simplificar Contrato
                    </button>
                  )}
                  {processingJob === 'simplify' && (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-2" />
                      <p className="text-sm text-slate-400">Traduciendo a lenguaje simple...</p>
                    </div>
                  )}
                  {simplifiedData && (
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <h4 className="text-sm font-medium text-blue-400 mb-2">Resumen Ejecutivo</h4>
                        <p className="text-sm text-slate-300">{simplifiedData.resumen_ejecutivo}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-slate-400 mb-2">Explicación Simple</h4>
                        <p className="text-sm text-slate-300 whitespace-pre-wrap">{simplifiedData.explicacion_simple}</p>
                      </div>
                      {simplifiedData.puntos_clave?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-400 mb-2">Puntos Clave</h4>
                          <ul className="space-y-1">
                            {simplifiedData.puntos_clave.map((punto, i) => (
                              <li key={i} className="flex items-start space-x-2 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                <span className="text-slate-300">{punto}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'bias' && (
                <div data-testid="bias-panel">
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-4 flex items-center space-x-2">
                    <Scale className="w-5 h-5 text-amber-400" />
                    <span>Análisis de Equidad</span>
                  </h3>
                  {!biasData && processingJob !== 'bias' && (
                    <button onClick={handleDetectBias} data-testid="run-bias-btn" className="btn-primary w-full py-2">
                      Detectar Sesgo
                    </button>
                  )}
                  {processingJob === 'bias' && (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-2" />
                      <p className="text-sm text-slate-400">Analizando equidad...</p>
                    </div>
                  )}
                  {biasData && (
                    <div className="space-y-4">
                      <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                        <div className="text-4xl font-bold text-amber-400 mb-1">{biasData.equity_score}</div>
                        <div className="text-sm text-slate-400">Puntaje de Equidad</div>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-sm text-slate-300">{biasData.overall_assessment}</p>
                      </div>
                      {biasData.biased_clauses?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-400 mb-2">Cláusulas Sesgadas:</h4>
                          <div className="space-y-2">
                            {biasData.biased_clauses.map((clause, i) => (
                              <div key={i} className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm">
                                <p className="text-amber-400 mb-1">{clause.issue}</p>
                                {clause.suggested_change && (
                                  <p className="text-slate-400 text-xs">Sugerencia: {clause.suggested_change}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'smart' && (
                <div data-testid="smart-panel">
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-4 flex items-center space-x-2">
                    <Cpu className="w-5 h-5 text-purple-400" />
                    <span>Smart Contract</span>
                  </h3>
                  {!smartContractData && processingJob !== 'smart' && (
                    <button onClick={handleSmartContract} data-testid="run-smart-btn" className="btn-primary w-full py-2">
                      Generar Smart Contract
                    </button>
                  )}
                  {processingJob === 'smart' && (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-2" />
                      <p className="text-sm text-slate-400">Generando Solidity...</p>
                    </div>
                  )}
                  {smartContractData && (
                    <div className="space-y-4">
                      <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                        <h4 className="text-sm font-medium text-purple-400 mb-1">{smartContractData.contract_name}</h4>
                        <p className="text-xs text-slate-400">{smartContractData.deployment_notes}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-slate-400 mb-2">Código Solidity:</h4>
                        <pre className="bg-slate-900 p-3 rounded-lg text-xs text-slate-300 overflow-x-auto max-h-60">
                          {smartContractData.solidity_code}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'verify' && (
                <div data-testid="verify-panel">
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-4 flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <span>Verificación de Activos</span>
                  </h3>
                  <p className="text-xs text-amber-400 bg-amber-400/10 p-2 rounded mb-4">
                    NOTA: Esta verificación está simulada con fines demostrativos.
                  </p>
                  {!verificationData && processingJob !== 'verify' && (
                    <button onClick={handleVerify} data-testid="run-verify-btn" className="btn-primary w-full py-2">
                      Verificar Activos
                    </button>
                  )}
                  {processingJob === 'verify' && (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-2" />
                      <p className="text-sm text-slate-400">Verificando...</p>
                    </div>
                  )}
                  {verificationData && (
                    <div className="space-y-4">
                      <div className={`p-3 rounded-lg ${
                        verificationData.overall_status === 'fully_verified' ? 'bg-emerald-500/10 border border-emerald-500/20' :
                        verificationData.overall_status === 'verified_with_warnings' ? 'bg-amber-500/10 border border-amber-500/20' :
                        'bg-rose-500/10 border border-rose-500/20'
                      }`}>
                        <span className="text-sm font-medium">
                          Estado: {verificationData.overall_status === 'fully_verified' ? 'Completamente Verificado' :
                                   verificationData.overall_status === 'verified_with_warnings' ? 'Verificado con Advertencias' :
                                   'Problemas Encontrados'}
                        </span>
                      </div>
                      {verificationData.statistics && (
                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div className="p-2 bg-slate-900/50 rounded">
                            <div className="text-xl font-bold text-blue-400">{verificationData.statistics.ruts_found}</div>
                            <div className="text-xs text-slate-400">RUTs encontrados</div>
                          </div>
                          <div className="p-2 bg-slate-900/50 rounded">
                            <div className="text-xl font-bold text-cyan-400">{verificationData.statistics.properties_found}</div>
                            <div className="text-xs text-slate-400">Propiedades</div>
                          </div>
                        </div>
                      )}
                      {verificationData.verifications?.map((v, i) => (
                        <div key={i} className="p-3 bg-slate-900/50 rounded-lg text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-slate-300">{v.entity || v.rut || v.type}</span>
                            <span className={v.status === 'verified' || v.status === 'compliant' || v.status === 'clean' ? 'status-success' : 'status-warning'}>
                              {v.status}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">{v.source}</p>
                        </div>
                      ))}
                      {verificationData.warnings?.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-amber-400">Advertencias:</h4>
                          {verificationData.warnings.map((w, i) => (
                            <div key={i} className="p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-300">
                              {w.message}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'visualize' && (
                <div data-testid="visualize-panel">
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-4 flex items-center space-x-2">
                    <GitBranch className="w-5 h-5 text-cyan-400" />
                    <span>Diagrama de Obligaciones</span>
                  </h3>
                  {!visualizationData && processingJob !== 'visualize' && (
                    <button onClick={handleVisualize} data-testid="run-visualize-btn" className="btn-primary w-full py-2">
                      Generar Visualización
                    </button>
                  )}
                  {processingJob === 'visualize' && (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-2" />
                      <p className="text-sm text-slate-400">Generando diagrama...</p>
                    </div>
                  )}
                  {visualizationData && (
                    <ObligationVisualizer data={visualizationData} />
                  )}
                </div>
              )}

              {activeTab === 'alerts' && (
                <div data-testid="contract-alerts-panel">
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-4 flex items-center space-x-2">
                    <Bell className="w-5 h-5 text-amber-400" />
                    <span>Alertas de Cumplimiento</span>
                  </h3>
                  {contractAlerts ? (
                    <div className="space-y-4">
                      {/* Compliance Score */}
                      {contractAlerts.compliance && (
                        <div className="p-4 bg-slate-900/50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-slate-400">Puntaje de Cumplimiento</span>
                            <span className={`text-2xl font-bold ${
                              contractAlerts.compliance.compliance_score >= 70 ? 'text-emerald-400' :
                              contractAlerts.compliance.compliance_score >= 40 ? 'text-amber-400' : 'text-rose-400'
                            }`}>
                              {contractAlerts.compliance.compliance_score}
                            </span>
                          </div>
                          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${
                                contractAlerts.compliance.compliance_score >= 70 ? 'bg-emerald-500' :
                                contractAlerts.compliance.compliance_score >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                              }`}
                              style={{ width: `${contractAlerts.compliance.compliance_score}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Alerts List */}
                      {contractAlerts.alerts?.length > 0 ? (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-slate-400">Alertas Relevantes:</h4>
                          {contractAlerts.alerts.map((alert, i) => (
                            <div key={i} className={`p-3 rounded-lg border ${
                              alert.impact === 'alto' ? 'bg-rose-500/10 border-rose-500/30' :
                              alert.impact === 'medio' ? 'bg-amber-500/10 border-amber-500/30' :
                              'bg-slate-500/10 border-slate-500/30'
                            }`}>
                              <div className="flex items-start justify-between">
                                <h5 className="font-medium text-sm text-white">{alert.title}</h5>
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  alert.impact === 'alto' ? 'bg-rose-500/20 text-rose-400' :
                                  alert.impact === 'medio' ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-slate-500/20 text-slate-400'
                                }`}>
                                  {alert.impact}
                                </span>
                              </div>
                              <p className="text-xs text-slate-400 mt-1">{alert.summary}</p>
                              {alert.action_required && (
                                <div className="mt-2 text-xs text-rose-400 flex items-center space-x-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  <span>Acción requerida</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
                          <p className="text-slate-400">No hay alertas para este contrato</p>
                        </div>
                      )}

                      {/* Recommendations */}
                      {contractAlerts.compliance?.recommendations?.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-slate-400">Recomendaciones:</h4>
                          {contractAlerts.compliance.recommendations.map((rec, i) => (
                            <div key={i} className="p-2 bg-blue-500/10 border border-blue-500/20 rounded text-sm">
                              <p className="text-blue-300">{rec.action}</p>
                              {rec.deadline && (
                                <p className="text-xs text-slate-400 mt-1">Plazo: {rec.deadline}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-2" />
                      <p className="text-sm text-slate-400">Cargando alertas...</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'versions' && (
                <div data-testid="versions-panel">
                  <div className="mb-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <GitBranch className="w-5 h-5 text-purple-400" />
                        <div>
                          <div className="font-medium text-purple-300">Comparador de Versiones</div>
                          <div className="text-sm text-slate-400">Ver evolución del riesgo y cambios visuales</div>
                        </div>
                      </div>
                      <Link
                        to={`/versiones/${contractId}`}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
                      >
                        Ver Comparador
                      </Link>
                    </div>
                  </div>
                  <VersionHistory 
                    contractId={contractId}
                    onVersionRestore={fetchContract}
                  />
                </div>
              )}

              {activeTab === 'blockchain' && (
                <div data-testid="blockchain-panel">
                  <BlockchainDeployment 
                    contractId={contractId}
                    smartContractData={smartContractData}
                  />
                </div>
              )}

              {activeTab === 'collaborators' && (
                <div data-testid="collaborators-panel">
                  <CollaborationPanel 
                    contractId={contractId}
                    isOwner={contract?.owner_id === user?.id}
                  />
                </div>
              )}

              {activeTab === 'deadlines' && (
                <div data-testid="deadlines-panel">
                  <ContractDeadlines contractId={contractId} />
                </div>
              )}

              {activeTab === 'notes' && (
                <div data-testid="notes-panel">
                  <ContractNotesAndTags contractId={contractId} />
                </div>
              )}

              {activeTab === 'activity' && (
                <div data-testid="activity-panel">
                  <ActivityLog contractId={contractId} />
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-lg animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold font-['Outfit']">Firma Digital</h3>
              <button onClick={() => setShowSignatureModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">Tu Nombre</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                data-testid="signer-name-input"
                placeholder="Nombre completo"
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-white"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">Dibuja tu Firma</label>
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                data-testid="signature-canvas"
                className="w-full border border-slate-700 rounded-lg cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <button
                onClick={clearSignature}
                data-testid="clear-signature-btn"
                className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Limpiar</span>
              </button>
              <button
                onClick={handleSign}
                data-testid="confirm-sign-btn"
                className="btn-primary px-6 py-2 flex items-center space-x-2"
              >
                <PenTool className="w-4 h-4" />
                <span>Firmar Documento</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Biometric Signature Modal */}
      {showBiometricModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-lg animate-slide-up">
            <BiometricSignature
              contractId={contractId}
              contractTitle={contract?.title}
              onSignatureComplete={handleBiometricSignatureComplete}
              onCancel={() => setShowBiometricModal(false)}
            />
          </div>
        </div>
      )}

      {/* Clause Advisor Panel */}
      {showClauseAdvisor && (
        <ClauseAdvisorPanel
          contractType={contract?.template_type || 'compraventa'}
          onClose={() => setShowClauseAdvisor(false)}
          onApplyClauses={handleApplyClauses}
        />
      )}

      {/* Chat Assistant with contract context */}
      <LegalChatAssistant 
        contractId={contractId} 
        contractTitle={contract?.title}
      />
    </div>
  );
};

export default ContractViewPage;
